/**
 * M14 AI Price Estimate — hand-written API function (Slice P1b, #412).
 *
 * The estimate endpoint (`GET /api/v1/pricing/estimate/`) is NOT in the generated
 * OpenAPI schema, so this is a hand-written call against the shared axios client
 * (`apiClient.request`). Unlike most of the app — which consumes the backend's
 * snake_case payloads verbatim — the design (§4.4 / N8) asks the estimate to be
 * mapped to camelCase so the chip reads `sourceLabel` / `isMarketReal` cleanly.
 * The mapper is kept LOCAL to this endpoint (a small field map, not a global
 * humps-style util) so it doesn't fight the rest of the codebase's snake_case
 * convention.
 *
 * Backend contract (P1a, fixed):
 *   GET /pricing/estimate/?part_type=<label>&ymm=<"YYYY Make Model">&condition=<value>
 *   200 JSON (Decimals are STRINGS):
 *     { min, typical, max, count, source_label, confidence, is_market_real }
 *     confidence ∈ { "ai", "market" }.
 *   204 (empty body) → no estimate available → resolve to `null` (chip hidden).
 *   400 → a required query param was missing (thrown as an axios error).
 */


/** snake_case → camelCase for the fixed estimate payload. Decimal fields
 * (`min`/`typical`/`max`) are passed through as STRINGS exactly as the backend
 * sends them — the chip formats, it does not do math. */
function mapEstimate(data) {
  return {
    min: data.min,
    typical: data.typical,
    max: data.max,
    count: data.count,
    sourceLabel: data.source_label,
    confidence: data.confidence,
    isMarketReal: data.is_market_real,
  };
}


/**
 * Fetch an advisory price estimate for a part.
 *
 * @param {object} args
 * @param {object} args.apiClient  the injected `useAPIClient()` client.
 * @param {string} args.partType   PartType LABEL string (e.g. "Headlight").
 * @param {string} args.ymm        "YYYY Make Model" (e.g. "2015 Toyota Camry").
 * @param {string} args.condition  PartCondition value (e.g. "used_good").
 * @param {AbortSignal} [args.signal]  passed through so TanStack Query can cancel.
 * @returns {Promise<object|null>} the camelCased estimate, or `null` on 204 /
 *   when any of the three inputs is blank (never fires the request then).
 */
async function getPriceEstimate({
  apiClient, partType, ymm, condition, signal,
}) {
  // Guard: the endpoint 400s on a missing param, and there is nothing to estimate
  // until all three are present — so don't even fire the request.
  if (!partType || !ymm || !condition) {
    return null;
  }

  const data = await apiClient.request({
    url: '/pricing/estimate/',
    method: 'GET',
    payload: { part_type: partType, ymm, condition },
    options: signal ? { signal } : {},
  });

  // 204 No Content → axios resolves with an empty body; the client returns it as
  // `''`/`undefined`. Either way there is no estimate → `null` (chip hidden).
  if (!data) {
    return null;
  }

  return mapEstimate(data);
}


export { getPriceEstimate, mapEstimate };
